/**
 * PlayErrorBoundary — playful safety net for ages 2-6.
 *
 * A failure inside a game/instrument must NOT leave the screen blank:
 * we catch it with a Solid `<ErrorBoundary>` and show a friendly fallback
 * (sad musical animal + simple message + big buttons). The child can
 * "Try again" (resets the subtree) or go to "Home".
 *
 * Theme-aware: uses tokens (joy palette + background/foreground/surface), respects
 * the global reduced-motion guard. Logs the error to console for diagnosis.
 */

import { type Component, ErrorBoundary, type JSX } from "solid-js";
import { Home, RotateCcw } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";

interface PlayErrorBoundaryProps {
  children: JSX.Element;
  /** Identifies the subtree that failed (game/instrument) for the log. */
  scope?: string;
}

/** Friendly musical animal (inline SVG, theme-aware via currentColor/tokens). */
function SadMascot() {
  return (
    <svg
      viewBox="0 0 120 120"
      class="h-32 w-32 drop-shadow-sm"
      role="img"
      aria-hidden="true"
      fill="none"
    >
      {/* cuerpo */}
      <circle cx="60" cy="62" r="40" class="fill-joy-sky" />
      {/* orejas */}
      <circle cx="30" cy="30" r="14" class="fill-joy-sky" />
      <circle cx="90" cy="30" r="14" class="fill-joy-sky" />
      <circle cx="30" cy="30" r="7" class="fill-joy-bubblegum" />
      <circle cx="90" cy="30" r="7" class="fill-joy-bubblegum" />
      {/* ojos (cerrados, gesto dormido/triste suave) */}
      <path d="M44 58 q6 6 12 0" class="stroke-zinc-700" stroke-width="3" stroke-linecap="round" />
      <path d="M64 58 q6 6 12 0" class="stroke-zinc-700" stroke-width="3" stroke-linecap="round" />
      {/* boquita */}
      <path d="M52 78 q8 -6 16 0" class="stroke-zinc-700" stroke-width="3" stroke-linecap="round" />
      {/* nota musical flotando */}
      <g class="animate-float">
        <circle cx="96" cy="74" r="5" class="fill-joy-tangerine" />
        <rect x="100" y="56" width="3" height="20" rx="1.5" class="fill-joy-tangerine" />
      </g>
    </svg>
  );
}

export const PlayErrorBoundary: Component<PlayErrorBoundaryProps> = (props) => {
  const { t } = useI18n();
  return (
    <ErrorBoundary
      fallback={(error, reset) => {
        console.error(`[PlayErrorBoundary${props.scope ? `:${props.scope}` : ""}]`, error);
        return (
          <div class="flex min-h-dvh flex-col items-center justify-center gap-6 bg-background p-6 text-center select-none">
            <div class="animate-pop-in">
              <SadMascot />
            </div>
            <div class="space-y-2">
              <h2 class="text-2xl font-bold tracking-tight text-foreground">
                {t(msg`Oops! The music stopped`)}
              </h2>
              <p class="mx-auto max-w-sm text-base text-muted">
                {t(msg`Something went wrong, but we can try again together.`)}
              </p>
            </div>
            <div class="flex flex-col items-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => {
                  reset();
                }}
                class="tile-3d tile-3d-press flex min-h-16 items-center gap-3 rounded-full bg-joy-grass px-8 text-lg font-bold text-emerald-950 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring"
              >
                <RotateCcw class="h-6 w-6" aria-hidden="true" />
                {t(msg`Try again`)}
              </button>
              <a
                href="/"
                class="tile-3d tile-3d-press flex min-h-16 items-center gap-3 rounded-full bg-joy-sunshine px-8 text-lg font-bold text-amber-950 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring"
              >
                <Home class="h-6 w-6" aria-hidden="true" />
                {t(msg`Home`)}
              </a>
            </div>
          </div>
        );
      }}
    >
      {props.children}
    </ErrorBoundary>
  );
};
